import { useState, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import AddressAutocomplete from '../AddressAutocomplete'
import { getAllDiveSites } from '../../services/diveSiteService'
import { getBrowserLocation } from '../../services/locationService'
import { haversineDistance } from '../../utils/distanceUtils'
import Database from '../../types/Database'

const DEFAULT_RADIUS_KM = 100

export type DiveSiteBrowserProps = {
  open: boolean
  onClose: () => void
  onSelect: (site: Database.DiveSite) => void
}

const DiveSiteBrowser = ({ open, onClose, onSelect }: DiveSiteBrowserProps) => {
  const [sites, setSites] = useState<Database.DiveSite[]>([])
  const [loading, setLoading] = useState(true)
  const [userLocation, setUserLocation] = useState<{
    lat: number
    lng: number
  } | null>(null)
  const [locatingUser, setLocatingUser] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [searchLocation, setSearchLocation] = useState<{
    lat: number
    lng: number
    displayName: string
  } | null>(null)

  // Fetch all dive sites once on open
  useEffect(() => {
    if (!open) return
    let mounted = true
    setLoading(true)
    getAllDiveSites()
      .then(data => {
        if (mounted) setSites(data)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [open])

  // Auto-detect user location on open
  useEffect(() => {
    if (!open || userLocation) return
    let mounted = true
    setLocatingUser(true)
    getBrowserLocation()
      .then(loc => {
        if (mounted) setUserLocation(loc)
      })
      .catch(() => {
        // Silently fail - user can set location manually
      })
      .finally(() => {
        if (mounted) setLocatingUser(false)
      })
    return () => {
      mounted = false
    }
  }, [open, userLocation])

  const activeLocation = searchLocation
    ? { lat: searchLocation.lat, lng: searchLocation.lng }
    : userLocation

  const nearbySites = useMemo(() => {
    if (!activeLocation) return sites
    return sites
      .map(site => ({
        ...site,
        distanceKm: haversineDistance(activeLocation, {
          lat: site.location.latitude,
          lng: site.location.longitude
        })
      }))
      .filter(s => s.distanceKm <= DEFAULT_RADIUS_KM)
      .sort((a, b) => a.distanceKm - b.distanceKm)
  }, [sites, activeLocation])

  const handleUseMyLocation = async () => {
    setLocatingUser(true)
    setLocationError(null)
    try {
      const loc = await getBrowserLocation()
      setUserLocation(loc)
      setSearchLocation(null)
    } catch (err) {
      setLocationError(
        err instanceof Error ? err.message : 'Failed to get location'
      )
    } finally {
      setLocatingUser(false)
    }
  }

  const handleSiteClick = (site: Database.DiveSite) => {
    onSelect(site)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
        Dive Sites Nearby
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label='Close'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <AddressAutocomplete
              value={searchLocation}
              onChange={place => {
                setSearchLocation(place)
                if (place) setLocationError(null)
              }}
            />
          </Box>
          <Tooltip title='Use my current location'>
            <IconButton
              onClick={handleUseMyLocation}
              disabled={locatingUser}
              color='primary'
              aria-label='Use my current location'
            >
              {locatingUser ? (
                <CircularProgress size={22} />
              ) : (
                <MyLocationIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {locationError && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            {locationError}
          </Alert>
        )}

        {!activeLocation && !loading && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Set a location to see dive sites within {DEFAULT_RADIUS_KM} km.
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : nearbySites.length === 0 ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ textAlign: 'center', py: 4 }}
          >
            {activeLocation
              ? `No dive sites within ${DEFAULT_RADIUS_KM} km`
              : 'No dive sites found'}
          </Typography>
        ) : (
          <List disablePadding>
            {nearbySites.map(site => (
              <ListItemButton
                key={site.id}
                onClick={() => handleSiteClick(site)}
              >
                <ListItemText
                  primary={site.name}
                  secondary={
                    <>
                      {site.address}
                      {'distanceKm' in site &&
                        ` \u2022 ${Math.round(
                          (site as any).distanceKm
                        )} km away`}
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default DiveSiteBrowser
